import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  BeakerIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Input,
  Label,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Select,
  SelectOption,
  Spinner,
} from '../ui';
import { cn } from '../../shared/lib/cn';
import {
  getGetMappingConfigApiTtcDataProductsMappingsConfigGetQueryKey,
  useGetMappingConfigApiTtcDataProductsMappingsConfigGet,
  useSaveEndpointMappingApiTtcDataProductsMappingsPost,
  useSaveSourceLogoApiTtcDataProductsSourcesSourceIdLogoPost,
  useTestEndpointMappingApiTtcDataProductsMappingsMappingIdTestPost,
  useValidateEndpointMappingApiTtcDataProductsMappingsMappingIdValidatePost,
} from '../../generated/ttc-data-products/ttc-data-products';
import type {
  DataProductEndpointMapping,
  DataProductFieldMapping,
  DataProductMappingConfigResponse,
  DataProductMappingSource,
  DataProductMappingTestResponse,
  DataProductMappingValidationResult,
  DataProductSchemaField,
  DataProductSchemaObject,
  DataProductSourceEndpoint,
  DataProductTransformRuleType,
} from '../../generated/models';

type TransformType = DataProductTransformRuleType;

const TRANSFORM_TYPES: TransformType[] = [
  'direct',
  'rename',
  'date_format',
  'normalize_case',
  'enum_map',
  'combine',
];

const statusVariant: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  healthy: 'success',
  valid: 'success',
  warning: 'warning',
  degraded: 'warning',
  invalid: 'danger',
  not_configured: 'default',
  draft: 'default',
};

function errorMessage(error: unknown): string {
  if (!error) return 'Unknown error';
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }
  return String(error);
}

function fieldsForEndpoint(endpoint?: DataProductSourceEndpoint): DataProductSchemaField[] {
  return endpoint?.fields ?? [];
}

function fieldsForSchema(schema?: DataProductSchemaObject): DataProductSchemaField[] {
  return schema?.fields ?? [];
}

function mappingClone(mapping: DataProductEndpointMapping): DataProductEndpointMapping {
  return {
    ...mapping,
    field_mappings: (mapping.field_mappings ?? []).map(field => ({
      ...field,
      source_fields: [...(field.source_fields ?? [])],
      transform: {
        type: field.transform?.type ?? 'direct',
        config: { ...(field.transform?.config ?? {}) },
      },
    })),
  };
}

function createDraftMapping(
  source: DataProductMappingSource,
  endpoint: DataProductSourceEndpoint,
  schema: DataProductSchemaObject,
): DataProductEndpointMapping {
  return {
    id: `${source.id}-${endpoint.id}-${schema.id}`,
    name: `${source.name} to ${schema.name}`,
    source_id: source.id,
    endpoint_id: endpoint.id,
    target_schema_id: schema.id,
    status: 'draft',
    sync_health: source.status,
    updated_at: new Date().toISOString(),
    updated_by: 'frontend',
    field_mappings: fieldsForSchema(schema).map(field => ({
      target_field: field.name,
      source_fields: [],
      transform: { type: 'direct', config: {} },
      required: field.required,
      notes: '',
    })),
  };
}

function sourceInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'TTC';
}

function JsonPreview({ value }: { value: unknown }) {
  return (
    <pre className="max-h-72 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-code text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

interface SourceListProps {
  sources: DataProductMappingSource[];
  selectedSourceId: string | null;
  onSelect: (sourceId: string) => void;
  logoDraft: string;
  onLogoDraftChange: (value: string) => void;
  onSaveLogo: () => void;
  savingLogo: boolean;
}

function SourceList({
  sources,
  selectedSourceId,
  onSelect,
  logoDraft,
  onLogoDraftChange,
  onSaveLogo,
  savingLogo,
}: SourceListProps) {
  const selectedSource = sources.find(source => source.id === selectedSourceId);

  return (
    <Panel className="h-full overflow-hidden">
      <PanelHeader>
        <PanelTitle className="flex items-center gap-2 text-base">
          <ServerStackIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          Sources
        </PanelTitle>
      </PanelHeader>
      <PanelBody className="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-3">
        <div className="space-y-2 overflow-auto pr-1">
          {sources.map(source => {
            const selected = source.id === selectedSourceId;
            return (
              <Button
                key={source.id}
                type="button"
                variant="ghost"
                onClick={() => onSelect(source.id)}
                className={cn(
                  'h-auto w-full justify-start rounded-md border px-3 py-3 text-left',
                  selected
                    ? 'border-eliza-red bg-eliza-red/10 text-charcoal dark:bg-eliza-red/20 dark:text-gray-100'
                    : 'border-gray-200 bg-white text-charcoal dark:border-dark-border dark:bg-dark-surface dark:text-gray-100',
                )}
              >
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  {source.logo_url ? (
                    <img src={source.logo_url} alt="" className="h-9 w-9 rounded-md object-contain" />
                  ) : (
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-micro font-semibold text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                      {sourceInitials(source.name)}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-small font-semibold">{source.name}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge variant={statusVariant[source.status] ?? 'default'}>{source.status}</Badge>
                      <span className="truncate text-micro text-gray-500 dark:text-gray-400">
                        {source.type}
                      </span>
                    </span>
                  </span>
                </span>
              </Button>
            );
          })}
        </div>

        {selectedSource && (
          <div className="border-t border-gray-200 pt-3 dark:border-dark-border">
            <Label htmlFor="ttc-source-logo">Logo URL</Label>
            <div className="mt-2 flex gap-2">
              <Input
                id="ttc-source-logo"
                value={logoDraft}
                onChange={event => onLogoDraftChange(event.target.value)}
                placeholder="https://..."
              />
              <Button
                type="button"
                variant="outline"
                onClick={onSaveLogo}
                disabled={savingLogo}
              >
                {savingLogo && <Spinner size="sm" className="mr-2" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

interface MappingListProps {
  mappings: DataProductEndpointMapping[];
  selectedMappingId: string | null;
  validation: DataProductMappingConfigResponse['validation'];
  onSelect: (mapping: DataProductEndpointMapping) => void;
  onCreate: () => void;
}

function MappingList({
  mappings,
  selectedMappingId,
  validation,
  onSelect,
  onCreate,
}: MappingListProps) {
  return (
    <Panel className="h-full overflow-hidden">
      <PanelHeader className="flex items-center justify-between gap-3">
        <PanelTitle className="flex items-center gap-2 text-base">
          <CircleStackIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          Mappings
        </PanelTitle>
        <Button type="button" size="sm" variant="outline" onClick={onCreate}>
          New
        </Button>
      </PanelHeader>
      <PanelBody className="h-full min-h-0 overflow-auto p-3">
        {mappings.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 p-4 text-small text-gray-500 dark:border-dark-border dark:text-gray-400">
            No mappings exist for this source yet.
          </div>
        ) : (
          <div className="space-y-2">
            {mappings.map(mapping => {
              const selected = mapping.id === selectedMappingId;
              const validationStatus = validation?.[mapping.id]?.status ?? mapping.status ?? 'draft';
              return (
                <Button
                  key={mapping.id}
                  type="button"
                  variant="ghost"
                  onClick={() => onSelect(mapping)}
                  className={cn(
                    'h-auto w-full justify-start rounded-md border px-3 py-3 text-left',
                    selected
                      ? 'border-eliza-red bg-eliza-red/10 dark:bg-eliza-red/20'
                      : 'border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-small font-semibold text-charcoal dark:text-gray-100">
                      {mapping.name}
                    </span>
                    <span className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant={statusVariant[validationStatus] ?? 'default'}>{validationStatus}</Badge>
                      <span className="truncate text-micro text-gray-500 dark:text-gray-400">
                        {mapping.field_mappings?.length ?? 0} fields
                      </span>
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

interface MappingEditorProps {
  mapping: DataProductEndpointMapping | null;
  endpoints: DataProductSourceEndpoint[];
  targetSchemas: DataProductSchemaObject[];
  selectedEndpoint?: DataProductSourceEndpoint;
  selectedSchema?: DataProductSchemaObject;
  onChange: (mapping: DataProductEndpointMapping) => void;
  onSave: () => void;
  onValidate: () => void;
  onTest: () => void;
  saving: boolean;
  validating: boolean;
  testing: boolean;
}

function MappingEditor({
  mapping,
  endpoints,
  targetSchemas,
  selectedEndpoint,
  selectedSchema,
  onChange,
  onSave,
  onValidate,
  onTest,
  saving,
  validating,
  testing,
}: MappingEditorProps) {
  if (!mapping) {
    return (
      <Panel className="h-full">
        <PanelBody className="flex h-full items-center justify-center p-8 text-center text-small text-gray-500 dark:text-gray-400">
          Select a mapping or create one for the selected source.
        </PanelBody>
      </Panel>
    );
  }

  const updateMapping = (patch: Partial<DataProductEndpointMapping>) => {
    onChange({ ...mapping, ...patch, updated_at: new Date().toISOString() });
  };

  const updateField = (index: number, patch: Partial<DataProductFieldMapping>) => {
    const nextFields = [...(mapping.field_mappings ?? [])];
    nextFields[index] = { ...nextFields[index], ...patch };
    updateMapping({ field_mappings: nextFields });
  };

  const addMissingTargetFields = () => {
    const existingTargets = new Set((mapping.field_mappings ?? []).map(field => field.target_field));
    const missingFields = fieldsForSchema(selectedSchema).filter(field => !existingTargets.has(field.name));
    updateMapping({
      field_mappings: [
        ...(mapping.field_mappings ?? []),
        ...missingFields.map(field => ({
          target_field: field.name,
          source_fields: [],
          transform: { type: 'direct' as TransformType, config: {} },
          required: field.required,
          notes: '',
        })),
      ],
    });
  };

  const sourceFieldOptions = fieldsForEndpoint(selectedEndpoint).map(field => field.name);

  return (
    <Panel className="h-full overflow-hidden">
      <PanelHeader className="flex items-center justify-between gap-3">
        <PanelTitle className="text-base">Mapping Editor</PanelTitle>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onValidate} disabled={validating}>
            {validating && <Spinner size="sm" className="mr-2" />}
            Validate
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onTest} disabled={testing}>
            {testing ? <Spinner size="sm" className="mr-2" /> : <BeakerIcon className="mr-2 h-4 w-4" />}
            Test
          </Button>
          <Button type="button" size="sm" onClick={onSave} disabled={saving}>
            {saving && <Spinner size="sm" className="mr-2" />}
            Save
          </Button>
        </div>
      </PanelHeader>
      <PanelBody className="h-full min-h-0 overflow-auto p-4">
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <Label htmlFor="ttc-mapping-name">Mapping name</Label>
            <Input
              id="ttc-mapping-name"
              className="mt-2"
              value={mapping.name}
              onChange={event => updateMapping({ name: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="ttc-endpoint">Source endpoint</Label>
            <Select
              value={mapping.endpoint_id}
              onValueChange={value => updateMapping({ endpoint_id: value })}
              placeholder="Endpoint"
              className="mt-2"
            >
              {endpoints.map(endpoint => (
                <SelectOption key={endpoint.id} value={endpoint.id}>
                  {endpoint.name}
                </SelectOption>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="ttc-target-schema">Target schema</Label>
            <Select
              value={mapping.target_schema_id}
              onValueChange={value => updateMapping({ target_schema_id: value })}
              placeholder="Schema"
              className="mt-2"
            >
              {targetSchemas.map(schema => (
                <SelectOption key={schema.id} value={schema.id}>
                  {schema.name}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
            <p className="text-small font-semibold text-charcoal dark:text-gray-100">
              {selectedEndpoint?.reference ?? 'No endpoint selected'}
            </p>
            <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
              {selectedEndpoint?.description ?? 'Choose a source endpoint to inspect available fields.'}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
            <p className="text-small font-semibold text-charcoal dark:text-gray-100">
              {selectedSchema?.source_contract ?? 'No target schema selected'}
            </p>
            <p className="mt-1 text-micro text-gray-500 dark:text-gray-400">
              {selectedSchema?.description ?? 'Choose a target schema to drive field coverage.'}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-small font-semibold text-charcoal dark:text-gray-100">Field mappings</h3>
            <p className="text-micro text-gray-500 dark:text-gray-400">
              Map source fields into the target data product contract.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addMissingTargetFields}>
            Add missing targets
          </Button>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full table-fixed border-separate border-spacing-0 text-left text-small">
            <thead>
              <tr className="text-micro uppercase text-gray-500 dark:text-gray-400">
                <th className="w-1/5 border-y border-l border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
                  Target
                </th>
                <th className="w-1/4 border-y border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
                  Source fields
                </th>
                <th className="w-1/5 border-y border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
                  Transform
                </th>
                <th className="w-1/4 border-y border-r border-gray-200 bg-gray-50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {(mapping.field_mappings ?? []).map((field, index) => (
                <tr key={`${field.target_field}-${index}`} className="align-top">
                  <td className="border-b border-l border-gray-200 px-3 py-2 dark:border-dark-border">
                    <div className="font-medium text-charcoal dark:text-gray-100">{field.target_field}</div>
                    {field.required && <Badge variant="warning" className="mt-1">required</Badge>}
                  </td>
                  <td className="border-b border-gray-200 px-3 py-2 dark:border-dark-border">
                    <Input
                      aria-label={`Source fields for ${field.target_field}`}
                      value={(field.source_fields ?? []).join(', ')}
                      onChange={event => updateField(index, {
                        source_fields: event.target.value.split(',').map(value => value.trim()).filter(Boolean),
                      })}
                      placeholder={sourceFieldOptions.slice(0, 2).join(', ') || 'field_name'}
                    />
                  </td>
                  <td className="border-b border-gray-200 px-3 py-2 dark:border-dark-border">
                    <Select
                      value={field.transform?.type ?? 'direct'}
                      onValueChange={value => updateField(index, {
                        transform: { type: value as TransformType, config: field.transform?.config ?? {} },
                      })}
                    >
                      {TRANSFORM_TYPES.map(type => (
                        <SelectOption key={type} value={type}>
                          {type}
                        </SelectOption>
                      ))}
                    </Select>
                  </td>
                  <td className="border-b border-r border-gray-200 px-3 py-2 dark:border-dark-border">
                    <Input
                      aria-label={`Notes for ${field.target_field}`}
                      value={field.notes ?? ''}
                      onChange={event => updateField(index, { notes: event.target.value })}
                      placeholder="Mapping notes"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PanelBody>
    </Panel>
  );
}

function ValidationPanel({
  validation,
  apiValidation,
}: {
  validation?: DataProductMappingValidationResult;
  apiValidation?: DataProductMappingValidationResult;
}) {
  const result = apiValidation ?? validation;

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle className="flex items-center gap-2 text-base">
          {result?.passed ? (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          ) : (
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
          )}
          Validation
        </PanelTitle>
      </PanelHeader>
      <PanelBody className="space-y-3 p-4">
        {!result ? (
          <p className="text-small text-gray-500 dark:text-gray-400">Validation has not run yet.</p>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Badge variant={statusVariant[result.status] ?? 'default'}>{result.status}</Badge>
              <span className="text-small text-gray-500 dark:text-gray-400">
                {result.passed ? 'Ready to save and publish.' : 'Review the issues below.'}
              </span>
            </div>
            {(result.issues ?? []).length === 0 ? (
              <p className="text-small text-gray-500 dark:text-gray-400">No validation issues.</p>
            ) : (
              <div className="space-y-2">
                {(result.issues ?? []).map((issue, index) => (
                  <div
                    key={`${issue.field ?? 'mapping'}-${index}`}
                    className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant={issue.severity === 'error' ? 'danger' : 'warning'}>
                        {issue.severity}
                      </Badge>
                      {issue.field && (
                        <span className="font-mono text-code text-charcoal dark:text-gray-100">
                          {issue.field}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-small text-gray-600 dark:text-gray-300">{issue.message}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

function TestPreviewPanel({ testResult }: { testResult?: DataProductMappingTestResponse }) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle className="text-base">Test Preview</PanelTitle>
      </PanelHeader>
      <PanelBody className="space-y-4 p-4">
        {!testResult ? (
          <p className="text-small text-gray-500 dark:text-gray-400">
            Run a test to preview normalized data product output.
          </p>
        ) : (
          <>
            <div>
              <p className="mb-2 text-micro font-semibold uppercase text-gray-500 dark:text-gray-400">
                Normalized output
              </p>
              <JsonPreview value={testResult.normalized_output} />
            </div>
            <div>
              <p className="mb-2 text-micro font-semibold uppercase text-gray-500 dark:text-gray-400">
                Sample source
              </p>
              <JsonPreview value={testResult.sample_source} />
            </div>
          </>
        )}
      </PanelBody>
    </Panel>
  );
}

export function TTCDataProductsWorkbench() {
  const queryClient = useQueryClient();
  const [selectedSourceId, setSelectedSourceId] = React.useState<string | null>(null);
  const [editorMapping, setEditorMapping] = React.useState<DataProductEndpointMapping | null>(null);
  const [apiValidation, setApiValidation] = React.useState<DataProductMappingValidationResult | undefined>();
  const [testResult, setTestResult] = React.useState<DataProductMappingTestResponse | undefined>();
  const [logoDraft, setLogoDraft] = React.useState('');

  const configQuery = useGetMappingConfigApiTtcDataProductsMappingsConfigGet();
  const saveMapping = useSaveEndpointMappingApiTtcDataProductsMappingsPost();
  const validateMapping = useValidateEndpointMappingApiTtcDataProductsMappingsMappingIdValidatePost();
  const testMapping = useTestEndpointMappingApiTtcDataProductsMappingsMappingIdTestPost();
  const saveLogo = useSaveSourceLogoApiTtcDataProductsSourcesSourceIdLogoPost();

  const config = configQuery.data;
  const sources = React.useMemo(() => config?.sources ?? [], [config?.sources]);
  const selectedSource = React.useMemo(
    () => sources.find(source => source.id === selectedSourceId) ?? sources[0],
    [selectedSourceId, sources],
  );
  const sourceEndpoints = React.useMemo(
    () => (config?.endpoints ?? []).filter(endpoint => endpoint.source_id === selectedSource?.id),
    [config?.endpoints, selectedSource?.id],
  );
  const sourceMappings = React.useMemo(
    () => (config?.mappings ?? []).filter(mapping => mapping.source_id === selectedSource?.id),
    [config?.mappings, selectedSource?.id],
  );
  const selectedEndpoint = sourceEndpoints.find(endpoint => endpoint.id === editorMapping?.endpoint_id);
  const selectedSchema = (config?.target_schemas ?? []).find(schema => schema.id === editorMapping?.target_schema_id);
  const selectedValidation = editorMapping ? config?.validation?.[editorMapping.id] : undefined;

  React.useEffect(() => {
    if (!config || sources.length === 0) return;
    if (!selectedSourceId) {
      setSelectedSourceId(sources[0].id);
      return;
    }
    if (!sources.some(source => source.id === selectedSourceId)) {
      setSelectedSourceId(sources[0].id);
    }
  }, [config, selectedSourceId, sources]);

  React.useEffect(() => {
    if (!selectedSource) return;
    setLogoDraft(selectedSource.logo_url ?? '');
  }, [selectedSource]);

  React.useEffect(() => {
    if (!selectedSource || editorMapping) return;
    if (sourceMappings[0]) {
      setEditorMapping(mappingClone(sourceMappings[0]));
    }
  }, [selectedSource, sourceMappings, editorMapping]);

  const createMapping = () => {
    if (!selectedSource || !sourceEndpoints[0] || !config?.target_schemas?.[0]) return;
    setApiValidation(undefined);
    setTestResult(undefined);
    setEditorMapping(createDraftMapping(selectedSource, sourceEndpoints[0], config.target_schemas[0]));
  };

  const selectMapping = (mapping: DataProductEndpointMapping) => {
    setApiValidation(undefined);
    setTestResult(undefined);
    setEditorMapping(mappingClone(mapping));
  };

  const saveCurrentMapping = () => {
    if (!editorMapping) return;
    saveMapping.mutate(
      { data: editorMapping },
      {
        onSuccess: saved => {
          setEditorMapping(mappingClone(saved));
          queryClient.invalidateQueries({
            queryKey: getGetMappingConfigApiTtcDataProductsMappingsConfigGetQueryKey(),
          });
        },
      },
    );
  };

  const validateCurrentMapping = () => {
    if (!editorMapping) return;
    validateMapping.mutate(
      { mappingId: editorMapping.id, data: editorMapping },
      { onSuccess: setApiValidation },
    );
  };

  const testCurrentMapping = () => {
    if (!editorMapping) return;
    testMapping.mutate(
      { mappingId: editorMapping.id, data: { mapping: editorMapping } },
      {
        onSuccess: result => {
          setApiValidation(result.validation);
          setTestResult(result);
        },
      },
    );
  };

  const saveSelectedLogo = () => {
    if (!selectedSource) return;
    saveLogo.mutate(
      { sourceId: selectedSource.id, data: { logo_url: logoDraft || null } },
      {
        onSuccess: () => queryClient.invalidateQueries({
          queryKey: getGetMappingConfigApiTtcDataProductsMappingsConfigGetQueryKey(),
        }),
      },
    );
  };

  if (configQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (configQuery.isError) {
    return (
      <div className="p-6">
        <Alert variant="error">
          <ExclamationTriangleIcon className="h-5 w-5" />
          <div>
            <p className="font-medium">Could not load TTC mapping configuration</p>
            <p className="mt-1 text-sm opacity-80">{errorMessage(configQuery.error)}</p>
          </div>
        </Alert>
      </div>
    );
  }

  if (!config || sources.length === 0) {
    return (
      <div className="p-6">
        <Panel>
          <PanelBody className="p-8 text-center">
            <CircleStackIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
            <h2 className="mt-3 text-small font-semibold text-charcoal dark:text-gray-100">
              No TTC mapping sources are configured
            </h2>
            <p className="mt-2 text-small text-gray-500 dark:text-gray-400">
              Backend mapping configuration returned no source systems.
            </p>
          </PanelBody>
        </Panel>
      </div>
    );
  }

  const mutationError = saveMapping.error || validateMapping.error || testMapping.error || saveLogo.error;

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 overflow-hidden p-4 xl:grid-cols-[280px_320px_minmax(0,1fr)_360px]">
      <SourceList
        sources={sources}
        selectedSourceId={selectedSource?.id ?? null}
        onSelect={sourceId => {
          setSelectedSourceId(sourceId);
          const nextMapping = (config.mappings ?? []).find(mapping => mapping.source_id === sourceId);
          setEditorMapping(nextMapping ? mappingClone(nextMapping) : null);
          setApiValidation(undefined);
          setTestResult(undefined);
        }}
        logoDraft={logoDraft}
        onLogoDraftChange={setLogoDraft}
        onSaveLogo={saveSelectedLogo}
        savingLogo={saveLogo.isPending}
      />

      <MappingList
        mappings={sourceMappings}
        selectedMappingId={editorMapping?.id ?? null}
        validation={config.validation}
        onSelect={selectMapping}
        onCreate={createMapping}
      />

      <div className="min-h-0">
        <MappingEditor
          mapping={editorMapping}
          endpoints={sourceEndpoints}
          targetSchemas={config.target_schemas ?? []}
          selectedEndpoint={selectedEndpoint}
          selectedSchema={selectedSchema}
          onChange={nextMapping => {
            setEditorMapping(nextMapping);
            setApiValidation(undefined);
            setTestResult(undefined);
          }}
          onSave={saveCurrentMapping}
          onValidate={validateCurrentMapping}
          onTest={testCurrentMapping}
          saving={saveMapping.isPending}
          validating={validateMapping.isPending}
          testing={testMapping.isPending}
        />
      </div>

      <div className="min-h-0 space-y-4 overflow-auto">
        {mutationError && (
          <Alert variant="error">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <div>
              <p className="font-medium">TTC mapping action failed</p>
              <p className="mt-1 text-sm opacity-80">{errorMessage(mutationError)}</p>
            </div>
          </Alert>
        )}
        {configQuery.isFetching && (
          <Alert variant="info">
            <ArrowPathIcon className="h-5 w-5" />
            <span>Refreshing mapping configuration.</span>
          </Alert>
        )}
        <ValidationPanel validation={selectedValidation} apiValidation={apiValidation} />
        <TestPreviewPanel testResult={testResult} />
      </div>
    </div>
  );
}
